import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Layers,
  FileText,
  Target,
  Calendar,
  User,
  ChevronDown,
  ChevronRight,
  X,
  Plus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  ENTITY_MODAL_TYPES,
  entityTypeToModalKey,
  type ModalEntityKey,
} from './entity-modal.constants';

// ============================================================
// ICON MAP
// ============================================================

export const ENTITY_ICONS: Record<ModalEntityKey, LucideIcon> = {
  task: Layers,
  note: FileText,
  objective: Target,
  event: Calendar,
  contact: User,
};

// ============================================================
// FIELD LABEL
// ============================================================

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-[11px] font-medium text-zinc-400 mb-1.5">{children}</label>;
}

// ============================================================
// MODAL INPUT
// ============================================================

interface ModalInputProps {
  placeholder?: string;
  type?: string;
  value?: string;
  onChange?: (val: string) => void;
  min?: string;
}

export function ModalInput({
  placeholder,
  type = 'text',
  value = '',
  onChange,
  min,
}: ModalInputProps) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      min={min}
      className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition"
    />
  );
}

// ============================================================
// MODAL TEXTAREA
// ============================================================

interface ModalTextareaProps {
  placeholder?: string;
  rows?: number;
  value?: string;
  onChange?: (val: string) => void;
  minHeight?: string;
}

export function ModalTextarea({
  placeholder,
  rows = 3,
  value = '',
  onChange,
  minHeight,
}: ModalTextareaProps) {
  return (
    <textarea
      rows={rows}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      style={minHeight ? { minHeight } : undefined}
      className="w-full bg-zinc-900/40 border border-zinc-600/45 rounded-[10px] px-3 py-2.5 text-[13px] text-zinc-200 placeholder:text-zinc-600 focus:border-violet-500/50 focus:bg-zinc-900/60 outline-none transition resize-vertical font-[inherit] leading-relaxed"
    />
  );
}

// ============================================================
// CUSTOM SELECT
// ============================================================

interface CustomSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (val: string) => void;
}

export function CustomSelect({ options, value, onChange }: CustomSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll so the dropdown doesn't detach from the trigger
  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.Event) => {
      // Don't close if the scroll originates from inside the dropdown itself
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  const selected = options.find((o) => o.value === value) || options[0];

  const getDropdownStyle = (): React.CSSProperties | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    };
  };

  const dropdownStyle = open ? getDropdownStyle() : null;

  return (
    <div ref={triggerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full bg-zinc-900/40 border rounded-[10px] px-3 py-2.5 text-[13px] outline-none transition cursor-pointer flex items-center justify-between text-left ${
          open ? 'border-violet-500/50 bg-zinc-900/60' : 'border-zinc-600/45'
        }`}
      >
        <span className={selected ? 'text-zinc-200' : 'text-zinc-500'}>
          {selected?.label || t('common.select')}
        </span>
        <ChevronDown
          size={12}
          className={`text-zinc-500 shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            className="bg-zinc-900 border border-zinc-600/50 rounded-[10px] shadow-xl max-h-[200px] overflow-y-auto"
            style={dropdownStyle}
          >
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-[13px] text-left cursor-pointer transition-colors ${
                  value === o.value
                    ? 'bg-violet-500/[0.12] text-violet-400'
                    : 'text-zinc-300 hover:bg-zinc-700/30'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// ============================================================
// PILL
// ============================================================

interface PillProps {
  label: string;
  color: string;
  active: boolean;
  onClick?: () => void;
}

export function Pill({ label, color, active, onClick }: PillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3.5 py-1.5 rounded-lg text-[11.5px] font-medium cursor-pointer transition-all whitespace-nowrap"
      style={
        active
          ? {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: color,
              backgroundColor: `${color}14`,
              color,
            }
          : {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'rgba(113,113,122,0.4)',
              backgroundColor: 'transparent',
              color: '#a1a1aa',
            }
      }
    >
      {label}
    </button>
  );
}

// ============================================================
// SECTION
// ============================================================

interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  accent?: string;
}

export function Section({ title, children, defaultOpen = true, accent }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-zinc-800/50 border border-zinc-700/30 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3.5 py-[11px] bg-transparent border-none cursor-pointer"
      >
        <span
          className="text-[11px] uppercase tracking-wide font-semibold"
          style={{ color: accent || '#a1a1aa' }}
        >
          {title}
        </span>
        <ChevronDown
          size={13}
          className={`text-zinc-500 transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
        />
      </button>
      {open && <div className="px-3.5 pb-3.5 pt-0.5">{children}</div>}
    </div>
  );
}

// ============================================================
// FIELD CARD
// ============================================================

interface FieldCardProps {
  accent: string;
  children: React.ReactNode;
}

export function FieldCard({ accent, children }: FieldCardProps) {
  return (
    <div
      className="rounded-xl p-3.5 flex flex-col gap-2.5"
      style={{
        backgroundColor: `${accent}0a`,
        border: `1px solid ${accent}2e`,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================
// PROGRESS BAR
// ============================================================

interface ProgressBarProps {
  done: number;
  total: number;
  accent: string;
}

export function ProgressBar({ done, total, accent }: ProgressBarProps) {
  const { t } = useTranslation();
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      className="rounded-xl p-3 px-3.5"
      style={{
        background: `linear-gradient(90deg, ${accent}1a 0%, transparent 100%)`,
        border: `1px solid ${accent}33`,
      }}
    >
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-[11px] font-semibold" style={{ color: accent }}>
          {t('entity.progress')}
        </span>
        <span className="text-[11px] text-zinc-400">
          {t('entity.progress_display', { done, total, percent: pct })}
        </span>
      </div>
      <div className="h-1 bg-zinc-600/30 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${pct}%`, backgroundColor: accent }}
        />
      </div>
    </div>
  );
}

// ============================================================
// TAG LIST
// ============================================================

interface TagListProps {
  tags: string[];
  accent: string;
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
}

export function TagList({ tags, accent, onAdd, onRemove }: TagListProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState('');

  const handleAdd = () => {
    const trimmed = newTag.trim();
    if (trimmed) {
      onAdd(trimmed);
      setNewTag('');
      setAdding(false);
    }
  };

  return (
    <div className="flex gap-1.5 flex-wrap items-center">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md"
          style={{
            backgroundColor: `${accent}1a`,
            border: `1px solid ${accent}40`,
            color: `${accent}cc`,
          }}
        >
          #{tag}
          <button
            type="button"
            onClick={() => onRemove(tag)}
            className="ml-0.5 hover:opacity-80 cursor-pointer"
            style={{ color: `${accent}99` }}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-transparent border border-dashed border-zinc-600/40 text-zinc-500 hover:text-zinc-300 cursor-pointer transition-colors"
        >
          {t('entity.add_tag')}
        </button>
      ) : (
        <div className="flex items-center gap-1.5">
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            autoFocus
            placeholder={t('entity.tag_placeholder')}
            className="bg-zinc-900/40 border border-zinc-600/45 rounded-md px-2 py-1 text-[11px] text-zinc-200 outline-none w-24 focus:border-violet-500/50 transition"
          />
          <button
            type="button"
            onClick={handleAdd}
            className="text-[11px] px-2 py-1 rounded-md font-medium text-white cursor-pointer"
            style={{ backgroundColor: accent }}
          >
            {t('common.validate')}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setNewTag('');
            }}
            className="text-zinc-500 hover:text-zinc-300 cursor-pointer"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// RELATION CHIP
// ============================================================

interface RelationChipProps {
  label: string;
  entityType: string;
  onClick?: () => void;
  onUnlink?: () => void;
  strikethrough?: boolean;
  subtitle?: string;
}

export function RelationChip({
  label,
  entityType,
  onClick,
  onUnlink,
  strikethrough,
  subtitle,
}: RelationChipProps) {
  const modalKey = entityTypeToModalKey(entityType);
  const config = ENTITY_MODAL_TYPES[modalKey];
  const Icon = ENTITY_ICONS[modalKey];

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-2 p-2 px-2.5 rounded-lg bg-zinc-900/30 border border-zinc-700/25 cursor-pointer transition-all hover:bg-zinc-700/15"
      style={{ ['--chip-accent' as string]: config.accent }}
    >
      <Icon size={14} style={{ color: config.accent }} className="shrink-0" />
      <div className="flex-1 min-w-0">
        <span
          className={`text-[11.5px] truncate block ${strikethrough ? 'line-through text-zinc-500' : 'text-zinc-300'}`}
        >
          {label}
        </span>
        {subtitle && <span className="text-[10px] text-zinc-500 block truncate">{subtitle}</span>}
      </div>
      {onUnlink && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnlink();
          }}
          className="hidden group-hover:flex text-zinc-600 hover:text-red-400 cursor-pointer transition-colors"
        >
          <X size={12} />
        </button>
      )}
      <ChevronRight size={11} className="text-zinc-600 shrink-0" />
    </div>
  );
}

// ============================================================
// ACTION BUTTON
// ============================================================

interface ActionBtnProps {
  label: string;
  color: string;
  onClick?: () => void;
}

export function ActionBtn({ label, color, onClick }: ActionBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-1.5 w-full p-2 rounded-lg bg-transparent border border-zinc-700/20 text-[11px] text-zinc-400 cursor-pointer transition-all"
      style={{
        ['--btn-color' as string]: color,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.color = color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.color = '';
      }}
    >
      <Plus size={10} className="shrink-0" />
      {label}
    </button>
  );
}
